import { useEffect, useState } from '/vendor/htm-preact.module.js';
import { get, onMutation } from '/api.js';

/**
 * A GET whose last answer paints immediately while the current one is fetched.
 *
 * Changing tabs used to blank the screen: the board replaced its contents with
 * "Loading…" for as long as the round trip took, and the circles list rendered
 * "You are not in a circle yet" before it had asked. Both are the app being
 * wrong on purpose while it waits.
 *
 * This is NOT the caching sw.js refuses to do, and the difference matters. The
 * service worker's rule is that a stale leaderboard must never be SERVED as
 * the answer, because it is wrong and it looks right. Here every read still
 * goes to the server, every time; what is held is only what to draw during the
 * wait, and it is replaced the moment the real answer lands. Nothing is ever
 * left showing data the server did not just confirm.
 *
 * Dropped whole on any write, for the same reason circlechoice.js is: a write
 * may have changed anything, and a list of caches for each write site to
 * remember is a list somebody will forget.
 */
const known = new Map();
const inflight = new Map();

// Clearing a cache is only half of it: a screen already showing the old answer
// has to be told to ask again, or it sits there until something remounts it.
// Creating a circle on the Circles tab is exactly that case — the write
// emptied the cache and the list carried on showing what it had.
let generation = 0;
const waiting = new Set();

onMutation(() => {
  known.clear();
  inflight.clear();
  generation += 1;
  waiting.forEach((fn) => fn(generation));
});

/**
 * One request per path, shared by everyone who asks while it is in the air.
 *
 * ONE cache, deliberately. A second store for the same path is two answers
 * that can disagree, and the first version of this had exactly that: the
 * circles list was held here and in circlechoice.js, so a screen reading one
 * paid for a round trip the other had already made.
 */
export function cachedGet(path) {
  if (inflight.has(path)) return inflight.get(path);
  const p = get(path)
    .then((v) => { known.set(path, v); inflight.delete(path); return v; })
    .catch((e) => { inflight.delete(path); throw e; });
  inflight.set(path, p);
  return p;
}

/** What is already known for a path, or undefined. */
export function peek(path) { return known.get(path); }

/** Warm a path without rendering it — for the tab you are about to open. */
export function prefetch(path) {
  if (!path || known.has(path) || inflight.has(path)) return;
  cachedGet(path).catch(() => {});
}

/**
 * [value, error]
 *
 * `value` is undefined only the very first time a path is read in a session —
 * the one moment there is genuinely nothing to show, and the only one where a
 * screen should draw a placeholder rather than an answer.
 *
 * `everyMs`, when given, ALSO re-reads on that interval — for a screen
 * showing something another device might change, where subscribing to this
 * device's own writes is not enough. `waiting`/`generation` are module-level,
 * one copy per browser TAB (`cueforge/web/live.js`'s FramePad note on a
 * second tab's read cache being independent of the first's is the same
 * fact): a write made on a different device — a second tablet scoring the
 * same fixture, a spectator's own phone — never bumps this tab's `seen`, so
 * without a poll this device is subscribed to a mutation stream that will
 * never carry the write it is waiting for. `kiosk.js`'s own 10s poll is the
 * precedent; this does NOT reuse `seen` for the poll, on purpose — see the
 * poll effect below for why a shared counter is the wrong shape here, not
 * just a redundant one.
 */
export function useRead(path, everyMs) {
  // Held WITH the path it answers. A write clears the cache and asks every
  // screen to look again — and clearing the VALUE at that moment sent the
  // screen back to its placeholder for a round trip, which unmounts whatever
  // it was drawing. That is how applying to join a circle lost the "Asked"
  // beside it: the section it was in was thrown away and rebuilt.
  const [held, setHeld] = useState(() => ({ path, value: known.get(path) }));
  const [error, setError] = useState(null);
  const [seen, setSeen] = useState(generation);
  const [tick, setTick] = useState(0);

  // Re-read after any write. Subscribing rather than polling, and by
  // generation rather than by path, because a write may have changed anything.
  useEffect(() => {
    const bump = (n) => setSeen(n);
    waiting.add(bump);
    return () => waiting.delete(bump);
  }, []);

  // The poll, on its OWN counter — not `seen`. `seen` carries the module's
  // `generation` value, set absolutely by `bump` above; a poll tick that
  // also wrote to `seen` (even as a plain `+1`, as this once did) can land
  // on the exact number `generation` reaches next, and React bails out of a
  // `setState` that repeats the current value — the effect below never
  // reruns, so that write's re-read is silently dropped. `tick` has no
  // meaning beyond "one more poll happened" and is on nobody else's number
  // line, so it can never coincide with `seen` and swallow a write.
  //
  // `tick` stays local to this hook rather than joining `generation`
  // itself: `generation` is module-wide, shared by every `useRead` in the
  // tab, so a bump from THIS screen's poll would force every OTHER
  // subscribed screen to re-read too, on every tick, whether or not
  // anything it reads could have changed. A local counter costs one extra
  // `useState`; a shared one costs every polling screen's poll interval
  // becoming every screen's.
  useEffect(() => {
    if (!everyMs) return undefined;
    const t = setInterval(() => setTick((n) => n + 1), everyMs);
    return () => clearInterval(t);
  }, [everyMs]);

  useEffect(() => {
    if (!path) return undefined;
    let live = true;
    // Only a change of PATH throws the old answer away — the previous path's
    // answer is not this one's. A re-read of the same path keeps what is on
    // screen until the new answer replaces it.
    setHeld((h) => (h.path === path ? h : { path, value: known.get(path) }));
    setError(null);
    cachedGet(path)
      .then((v) => { if (live) { setHeld({ path, value: v }); setError(null); } })
      .catch((e) => { if (live) setError(e); });
    return () => { live = false; };
  }, [path, seen, tick]);

  return [held.path === path ? held.value : undefined, error];
}
